import { useState, useEffect } from 'react';
import { InputDurationElement } from '@app/common';
import TextWithReferences from '../../TextWithReferences/TextWithReferences';
import RequiredAsterisk from '../RequiredAsterisk';

type DurationValue = {
    hours: number;
    minutes: number;
    seconds: number;
};

type DurationState = {
    hours: string;
    minutes: string;
    seconds: string;
};

type Properties = {
    data: InputDurationElement & {
        disableMinutes?: boolean;
        disableSeconds?: boolean;
    };
    value?: string; // JSON stringified DurationValue
    setValue: (value: string) => void;
};

const InputDuration = (props: Properties) => {
    const defaultParsed: DurationState = {
        hours: '',
        minutes: '',
        seconds: '',
    };

    const initial: DurationState = (() => {
        try {
            if (props.value) {
                const parsed: DurationValue = JSON.parse(props.value);
                return {
                    hours: parsed.hours !== 0 ? String(parsed.hours) : '',
                    minutes: parsed.minutes !== 0 ? String(parsed.minutes) : '',
                    seconds: parsed.seconds !== 0 ? String(parsed.seconds) : '',
                };
            }
            return defaultParsed;
        } catch {
            return defaultParsed;
        }
    })();

    const [inputs, setInputs] = useState<DurationState>(initial);

    // Update if external props.value changes
    useEffect(() => {
        try {
            if (props.value) {
                const parsed: DurationValue = JSON.parse(props.value);
                setInputs({
                    hours: parsed.hours !== 0 ? String(parsed.hours) : '',
                    minutes: parsed.minutes !== 0 ? String(parsed.minutes) : '',
                    seconds: parsed.seconds !== 0 ? String(parsed.seconds) : '',
                });
            } else {
                setInputs(defaultParsed);
            }
        } catch {
            // ignore parse errors
        }
    }, [props.value]);

    const handleChange = (part: keyof DurationValue, val: string) => {
        if (val === '' || /^-?\d*$/.test(val)) {
            setInputs(prev => ({
                ...prev,
                [part]: val,
            }));
        }
    };

    const handleBlur = () => {
        const relevantFields = ['hours'];

        if (!props.data.disableMinutes) {
            relevantFields.push('minutes');
        }
        if (!props.data.disableSeconds && !props.data.disableMinutes) {
            relevantFields.push('seconds');
        }

        const parsed: DurationValue = {
            hours: parseInt(inputs.hours) || 0,
            minutes: parseInt(inputs.minutes) || 0,
            seconds: parseInt(inputs.seconds) || 0,
        };

        props.setValue(JSON.stringify(parsed));
    };

    return (
        <fieldset className="page-input page-input-duration">
            <label>
                <TextWithReferences text={props.data.label || ''} />
                <RequiredAsterisk required={props.data.required} />
            </label>

            <div className="page-input-duration-inner">
                <div className="page-input-duration-section">
                    <label>Stunden</label>
                    <input
                        type="number"
                        value={inputs.hours}
                        onChange={e => handleChange('hours', e.target.value)}
                        onBlur={handleBlur}
                        step={1}
                        min={0}
                        inputMode="numeric"
                    />
                </div>

                {!props.data.disableMinutes && (
                    <div className="page-input-duration-section">
                        <label>Minuten</label>
                        <input
                            type="number"
                            value={inputs.minutes}
                            onChange={e => handleChange('minutes', e.target.value)}
                            onBlur={handleBlur}
                            step={1}
                            min={0}
                            max={59}
                            inputMode="numeric"
                        />
                    </div>
                )}

                {!props.data.disableSeconds && !props.data.disableMinutes && (
                    <div className="page-input-duration-section">
                        <label>Sekunden</label>
                        <input
                            type="number"
                            value={inputs.seconds}
                            onChange={e => handleChange('seconds', e.target.value)}
                            onBlur={handleBlur}
                            step={1}
                            min={0}
                            max={59}
                            inputMode="numeric"
                        />
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default InputDuration;
